import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../services/api';

const PAGE_SIZE = 10;
const EXPORT_PAGE = 50;
const EXPORT_MAX = 2000;

const ACK_TIP = 'The device accepted this command and sent an ack.';
const NACK_TIP = 'The device did not acknowledge this command.';

const RANGE_OPTIONS = [
  { id: 'all', label: 'Any time' },
  { id: '1h', label: 'Last hour' },
  { id: '4h', label: 'Last 4 hours' },
  { id: '12h', label: 'Last 12 hours' },
  { id: '24h', label: 'Last 24 hours' },
  { id: 'custom', label: 'Custom date' },
];

const rangeStart = (id, now = new Date()) => {
  if (id === '1h') return new Date(now.getTime() - 60 * 60 * 1000);
  if (id === '4h') return new Date(now.getTime() - 4 * 60 * 60 * 1000);
  if (id === '12h') return new Date(now.getTime() - 12 * 60 * 60 * 1000);
  if (id === '24h') return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return null;
};

const startOfLocalDay = (dateValue) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue || '');
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const endOfLocalDay = (dateValue) => {
  const start = startOfLocalDay(dateValue);
  if (!start) return null;
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
};

const customRangeBounds = (fromValue, toValue) => {
  const start = startOfLocalDay(fromValue);
  const end = endOfLocalDay(toValue);
  if (!start || !end) return null;
  return { since: start.toISOString(), until: end.toISOString() };
};

const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const commandsToCsv = (rows) => {
  const header = [
    'submitted',
    'command',
    'status',
    'ack',
    'status_error',
    'status_text',
    'submitted_by',
    'execution_time_ms',
    'uuid',
    'details',
  ];
  const lines = [header.join(',')];
  rows.forEach((cmd) => {
    lines.push([
      cmd.submitted || '',
      cmd.command || '',
      cmd.status || '',
      cmd.has_response ? 'Ack' : 'Nack',
      cmd.status_error || '',
      cmd.status_text || '',
      cmd.submitted_by || '',
      cmd.execution_time_ms || '',
      cmd.uuid || '',
      cmd.details || '',
    ].map(csvCell).join(','));
  });
  return `\uFEFF${lines.join('\r\n')}`;
};

const downloadCommandsCsv = (serial, range, rows) => {
  const blob = new Blob([commandsToCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `commands-${serial || 'device'}-${range || 'all'}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const pageWithinRange = (commands, sinceIso, untilIso) => {
  const start = sinceIso ? new Date(sinceIso).getTime() : null;
  const until = untilIso ? new Date(untilIso).getTime() : null;
  if (start == null && until == null) return { page: commands, reachedEnd: false };
  const page = [];
  for (const cmd of commands) {
    const submitted = new Date(cmd?.submitted).getTime();
    if (Number.isNaN(submitted) || (start != null && submitted < start)) {
      return { page, reachedEnd: true };
    }
    if (until != null && submitted >= until) continue;
    page.push(cmd);
  }
  return { page, reachedEnd: false };
};

const STATUS_TIP = {
  completed: 'The device finished this command without an error.',
  failed: 'The device reported an error, or the command never reached it.',
  pending: 'The command was sent and has not finished yet.',
};

const statusColor = (status) => {
  if (status === 'failed') return 'error';
  if (status === 'completed') return 'success';
  return 'warning';
};

const statusTip = (status) => STATUS_TIP[status] || STATUS_TIP.pending;

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const commandColumns = {
  display: 'grid',
  gridTemplateColumns: '28px minmax(140px, 1fr) 108px 64px 168px',
  columnGap: 1,
  alignItems: 'center',
};

const statusNote = (cmd) => {
  const text = (cmd.status_text || '').trim();
  const genericSuccess = /^success$/i.test(text);
  if (cmd.status_error) {
    return genericSuccess || !text ? `error ${cmd.status_error}` : `error ${cmd.status_error} — ${text}`;
  }
  if (!text || genericSuccess) return '';
  return text;
};

const CommandRow = ({ cmd }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  const hasDetails = Boolean(cmd.details);
  const note = statusNote(cmd);
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);
  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(cmd.details || '');
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      setCopied(false);
    }
  };
  return (
    <Box sx={{ px: 1, py: 0.75 }}>
      <Box sx={commandColumns}>
        {hasDetails ? (
          <IconButton
            size="small"
            aria-expanded={open}
            aria-label={open ? 'Hide command details' : 'Show command details'}
            onClick={() => setOpen((value) => !value)}
            sx={{
              p: 0.25,
              justifySelf: 'center',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        ) : <Box />}
        <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
          {cmd.command || '—'}
        </Typography>
        <Tooltip title={statusTip(cmd.status)}>
          <Chip size="small" color={statusColor(cmd.status)} label={cmd.status || 'pending'} sx={{ height: 20, width: 92, fontSize: 11 }} />
        </Tooltip>
        <Tooltip title={cmd.has_response ? ACK_TIP : NACK_TIP}>
          <Chip
            size="small"
            variant="outlined"
            label={cmd.has_response ? 'Ack' : 'Nack'}
            sx={{ height: 20, width: 56, fontSize: 11 }}
          />
        </Tooltip>
        <Typography variant="caption" color="text.secondary" noWrap>
          {formatWhen(cmd.submitted)}
        </Typography>
      </Box>
      {note ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {note}
        </Typography>
      ) : null}
      {hasDetails ? (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ position: 'relative', mt: 0.75 }}>
            <Tooltip title={copied ? 'Copied' : 'Copy command data'}>
              <IconButton
                size="small"
                aria-label="Copy command data"
                onClick={copyDetails}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                pr: 4.5,
                maxHeight: 280,
                overflow: 'auto',
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {cmd.details}
            </Box>
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
};

const CommandHistory = ({ serial }) => {
  const [commands, setCommands] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('all');
  const [hasResponse, setHasResponse] = useState('all');
  const [commandOptions, setCommandOptions] = useState([]);
  const [selectedCommands, setSelectedCommands] = useState([]);
  const [range, setRange] = useState('4h');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [since, setSince] = useState(() => rangeStart('4h').toISOString());
  const [until, setUntil] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const sentinelRef = useRef(null);
  const requestGen = useRef(0);
  const includeTypesRef = useRef(true);
  const seenSerial = useRef(serial);
  if (seenSerial.current !== serial) {
    seenSerial.current = serial;
    includeTypesRef.current = true;
    setCommandOptions([]);
    setSelectedCommands([]);
  }

  const loadPage = useCallback(async ({ append, before, beforeId }) => {
    if (!serial) return;
    const gen = requestGen.current + 1;
    requestGen.current = gen;
    setLoading(true);
    setError('');
    const includeTypes = includeTypesRef.current;
    try {
      const data = await api.getDeviceCommands(serial, {
        limit: PAGE_SIZE,
        before: before || until || undefined,
        beforeId: before ? beforeId : undefined,
        command: selectedCommands,
        since,
        status,
        hasResponse: hasResponse === 'all' ? undefined : hasResponse === 'yes',
        includeTypes,
      });
      if (includeTypes) includeTypesRef.current = false;
      if (gen !== requestGen.current) return;
      if (Array.isArray(data?.command_types)) {
        setCommandOptions(data.command_types);
      }
      const { page, reachedEnd } = pageWithinRange(data?.commands || [], since, until);
      const more = !!data?.has_more && !reachedEnd;
      setCommands((prev) => (append ? [...prev, ...page] : page));
      setHasMore(more);
      setCursor(more ? { before: data.next_before, beforeId: data.next_before_id } : null);
    } catch (err) {
      if (gen !== requestGen.current) return;
      if (includeTypes) includeTypesRef.current = true;
      setError(err?.message || 'Failed to load command history');
    } finally {
      if (gen === requestGen.current) setLoading(false);
    }
  }, [serial, selectedCommands, since, until, status, hasResponse]);

  useEffect(() => {
    setCommands([]);
    setCursor(null);
    setHasMore(false);
    loadPage({ append: false });
  }, [loadPage]);

  const exportCsv = useCallback(async () => {
    if (!serial || exporting) return;
    setExporting(true);
    setExportNote('');
    setError('');
    try {
      const rows = [];
      let before = until || undefined;
      let beforeId;
      let more = true;
      while (more && rows.length < EXPORT_MAX) {
        const data = await api.getDeviceCommands(serial, {
          limit: EXPORT_PAGE,
          before,
          beforeId,
          command: selectedCommands,
          since,
          status,
          hasResponse: hasResponse === 'all' ? undefined : hasResponse === 'yes',
        });
        const { page, reachedEnd } = pageWithinRange(data?.commands || [], since, until);
        rows.push(...page);
        const nextBefore = data?.next_before;
        const nextBeforeId = data?.next_before_id;
        more = !!data?.has_more && !reachedEnd && !!nextBefore && (nextBefore !== before || nextBeforeId !== beforeId);
        before = nextBefore;
        beforeId = nextBeforeId;
      }
      const limited = rows.slice(0, EXPORT_MAX);
      downloadCommandsCsv(serial, range, limited);
      if (rows.length > EXPORT_MAX) {
        setExportNote(`Exported the first ${EXPORT_MAX} commands that match the current filters.`);
      }
    } catch (err) {
      setError(err?.message || 'Failed to export commands');
    } finally {
      setExporting(false);
    }
  }, [serial, exporting, selectedCommands, since, until, status, hasResponse, range]);

  const loadMore = useCallback(() => {
    if (!hasMore || !cursor || loading) return;
    loadPage({ append: true, before: cursor.before, beforeId: cursor.beforeId });
  }, [hasMore, cursor, loading, loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    }, { rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const sortedCommands = useMemo(() => {
    const rows = [...commands];
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'command') {
        cmp = (a.command || '').localeCompare(b.command || '', undefined, { sensitivity: 'base' });
      } else if (sortBy === 'status') {
        cmp = (a.status || 'pending').localeCompare(b.status || 'pending', undefined, { sensitivity: 'base' });
      } else {
        cmp = new Date(a.submitted).getTime() - new Date(b.submitted).getTime();
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [commands, sortBy, sortDir]);

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDir(column === 'date' ? 'desc' : 'asc');
  };

  const sortHeader = (column, label) => (
    <Button
      size="small"
      onClick={() => toggleSort(column)}
      sx={{
        justifyContent: 'flex-start',
        minWidth: 0,
        px: 0,
        py: 0,
        color: sortBy === column ? 'text.primary' : 'text.secondary',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.2,
        textTransform: 'none',
      }}
    >
      {label}{sortBy === column ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </Button>
  );

  const filterControlSx = {
    '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 12, textTransform: 'none', lineHeight: 1.6 },
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Commands</Typography>
        <Tooltip title="Download every command that matches the current filters.">
          <span>
            <Button
              size="small"
              onClick={exportCsv}
              disabled={!serial || exporting || loading}
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25, flexWrap: 'wrap' }} useFlexGap>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={status}
          onChange={(_, value) => { if (value) setStatus(value); }}
          sx={filterControlSx}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="failed" sx={{ p: 0 }}>
            <Tooltip title={STATUS_TIP.failed}>
              <Box component="span" sx={{ px: 1, py: 0.25 }}>Failed</Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="completed" sx={{ p: 0 }}>
            <Tooltip title={STATUS_TIP.completed}>
              <Box component="span" sx={{ px: 1, py: 0.25 }}>Completed</Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="pending" sx={{ p: 0 }}>
            <Tooltip title={STATUS_TIP.pending}>
              <Box component="span" sx={{ px: 1, py: 0.25 }}>Pending</Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={hasResponse}
          onChange={(_, value) => { if (value) setHasResponse(value); }}
          sx={filterControlSx}
        >
          <ToggleButton value="all" sx={{ p: 0 }}>
            <Tooltip title="All commands, whether or not the device acknowledged them.">
              <Box component="span" sx={{ px: 1, py: 0.25 }}>Any</Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="yes" sx={{ p: 0 }}>
            <Tooltip title={ACK_TIP}>
              <Box component="span" sx={{ px: 1, py: 0.25 }}>Ack</Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="no" sx={{ p: 0 }}>
            <Tooltip title={NACK_TIP}>
              <Box component="span" sx={{ px: 1, py: 0.25 }}>Nack</Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="When"
          value={range}
          onChange={(event) => {
            const id = event.target.value;
            setRange(id);
            if (id === 'custom') {
              const bounds = customRangeBounds(customFrom, customTo);
              if (bounds) {
                setSince(bounds.since);
                setUntil(bounds.until);
              }
              return;
            }
            setUntil('');
            const start = rangeStart(id);
            setSince(start ? start.toISOString() : '');
          }}
          sx={{ width: 168 }}
        >
          {RANGE_OPTIONS.map((option) => (
            <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
          ))}
        </TextField>
        {range === 'custom' && (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={customFrom}
              onChange={(event) => {
                const value = event.target.value;
                setCustomFrom(value);
                const bounds = customRangeBounds(value, customTo);
                if (!bounds) return;
                setSince(bounds.since);
                setUntil(bounds.until);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={customTo}
              onChange={(event) => {
                const value = event.target.value;
                setCustomTo(value);
                const bounds = customRangeBounds(customFrom, value);
                if (!bounds) return;
                setSince(bounds.since);
                setUntil(bounds.until);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
          </>
        )}
        <Autocomplete
          multiple
          size="small"
          limitTags={1}
          disableCloseOnSelect
          options={commandOptions}
          value={selectedCommands}
          onChange={(_, value) => setSelectedCommands(value)}
          renderTags={(value, getTagProps) => value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option} size="small" label={option} sx={{ height: 20, maxWidth: 110 }} />
          ))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type"
              placeholder={selectedCommands.length === 0 ? 'All' : ''}
            />
          )}
          sx={{ width: 200 }}
        />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {exportNote && <Alert severity="info" sx={{ mb: 1.5 }}>{exportNote}</Alert>}
      {!loading && commands.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary">No commands match.</Typography>
      )}
      {commands.length > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto' }}>
          <Box sx={{ minWidth: 560 }}>
            <Box sx={{ ...commandColumns, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box />
              {sortHeader('command', 'Command')}
              {sortHeader('status', 'Status')}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: 11 }}>Ack</Typography>
              {sortHeader('date', 'Date')}
            </Box>
            {sortedCommands.map((cmd, index) => (
              <Box
                key={cmd.uuid}
                sx={{ borderBottom: index < sortedCommands.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
              >
                <CommandRow cmd={cmd} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <Box ref={sentinelRef} sx={{ height: 1 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
        {loading && <CircularProgress size={18} />}
        {hasMore && (
          <Button size="small" variant="outlined" onClick={loadMore} disabled={loading}>
            Next 10
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default CommandHistory;
